// Angle.jsx
import React, { useState, useMemo, useRef } from "react";
import axios from "axios";
import "./Angle.css";

/** ===================== CONFIG ===================== */
const MODEL = "gemini-2.5-flash"; // swap to "gemini-2.5-pro" for richer prose

/** Balanced outlet sets so we don’t drift to “Center only” */
const LEFT_DOMAINS = [
  // US
  "theguardian.com","vox.com","huffpost.com","msnbc.com","slate.com",
  "motherjones.com","theatlantic.com","newyorker.com","thedailybeast.com",
  "salon.com","rollingstone.com","propublica.org",
  // Big US moved from Center -> Left
  "cnn.com","npr.org","axios.com","washingtonpost.com","nytimes.com",
  "abcnews.go.com","nbcnews.com","cbsnews.com","cnbc.com",
  // International / other
  "theconversation.com","lemonde.fr","spiegel.de","thewire.in",
  // South Asia
  "scroll.in","newsclick.in","thequint.com","altnews.in","caravanmagazine.in",
  "dawn.com","tribune.com.pk",
  // Middle East
  "alaraby.co.uk","themedialine.org",
  // Turkey
  "bianet.org","duvarenglish.com",
  // Bangladesh
  "dhakatribune.com",
  // Indonesia
  "jakartaglobe.id"
];

const CENTER_DOMAINS = [
  // Wires / core
  "reuters.com","apnews.com",
  // Major globals commonly near center
  "bbc.com","bbc.co.uk","bloomberg.com","ft.com","economist.com",
  // US nationals typically kept in Center
  "thehill.com","newsweek.com","time.com","usatoday.com",
  "marketwatch.com","forbes.com","news.yahoo.com","latimes.com","c-span.org",
  "statnews.com","technologyreview.com",
  // (Optional) WSJ to Center if you want news-only placement:
  // "wsj.com",
  // International
  "france24.com","dw.com","rte.ie","cbc.ca","theglobeandmail.com",
  "straitstimes.com","scmp.com","nikkei.com","rtve.es","elpais.com",
  "independent.co.uk","abc.net.au","zeit.de",
  // South Asia
  "ndtv.com","indiatoday.in","hindustantimes.com","thehindu.com",
  "livemint.com","timesofindia.indiatimes.com","geo.tv","theprint.in","telegraphindia.com",
  // Middle East
  "thenationalnews.com",
  // Turkey
  "hurriyetdailynews.com",
  // Bangladesh
  "thedailystar.net","bdnews24.com",
  // Indonesia
  "thejakartapost.com","tempo.co"
];

const RIGHT_DOMAINS = [
  // US
  "foxnews.com",
  // If you keep WSJ mixed (news+editorial), leave it here; otherwise move to CENTER above.
  "wsj.com",
  "nypost.com","washingtonexaminer.com","nationalreview.com",
  "thedailywire.com","theblaze.com","newsmax.com","thefederalist.com","freebeacon.com",
  "townhall.com","washingtontimes.com","pjmedia.com","spectator.org",
  "theamericanconservative.com","city-journal.org","nysun.com",
  // International
  "telegraph.co.uk","spectator.co.uk","dailymail.co.uk","thetimes.co.uk",
  "nationalpost.com","skynews.com.au","theaustralian.com.au",
  "diewelt.de","faz.net","yomiuri.co.jp","lefigaro.fr","elmundo.es","israelhayom.com",
  // South Asia
  "opindia.com","republicworld.com","firstpost.com","news18.com","zeenews.india.com",
  "swarajyamag.com","arynews.tv","nation.com.pk",
  // Middle East
  "arabnews.com","asharq.com",
  // Turkey
  "dailysabah.com",
  // Indonesia
  "kompas.id",
];

const ALL_DOMAINS = [...new Set([...LEFT_DOMAINS, ...CENTER_DOMAINS, ...RIGHT_DOMAINS])].join(",");

const DOMAIN_HINTS = {
  ...Object.fromEntries(LEFT_DOMAINS.map(d => [d, "left"])),
  ...Object.fromEntries(RIGHT_DOMAINS.map(d => [d, "right"])),
  ...Object.fromEntries(CENTER_DOMAINS.map(d => [d, "center"])),
};

const DEMO_CACHE = {
  "U.S. government shutdown": {
    summaries: {
      left:
        "The U.S. government shutdown has entered its third or fourth day with no resolution in sight, and is expected to continue into next week due to stalled negotiations. Left-leaning coverage emphasizes the significant impact on furloughed federal workers, citing specific examples, and highlights the broader national uncertainty. Some articles attribute the shutdown to specific conservative figures and initiatives, such as Russell Vought and Project 2025, suggesting a deliberate strategy to disrupt government. While noting the delay of crucial economic data like the jobs report, some outlets also observe that stock markets are resilient and Bitcoin is gaining 'safe haven status' amidst the political deadlock.",
      center:
        "Centrist media reports confirm the U.S. government shutdown is active, with the White House, under Donald Trump, intensifying pressure on Democrats by threatening mass layoffs of federal workers and cutting specific projects. Coverage details immediate operational impacts, such as the suspension of E-Verify, the furloughing of 11,000 FAA employees, and the freezing of clean energy initiatives and New York funding. These outlets also highlight the economic uncertainty reflected in skyrocketing gold and silver prices. Importantly, centrist reporting actively debunks Republican claims that Democrats are advocating for free healthcare for undocumented immigrants, clarifying that the debate centers on extending tax credits",
      right:
        "Right-leaning media coverage of the U.S. government shutdown primarily focuses on practical implications for citizens, particularly concerning travel. Articles advise on potential disruptions to travel plans, including longer airport wait times and delays, and suggest alternatives like driving for Canadians. A significant emphasis is placed on reassuring the public regarding immigration and border security, with U.S. Immigration and Customs Enforcement (ICE) vowing 'no change' to its policies despite the shutdown. This coverage tends to frame the event as an inconvenience requiring practical advice, rather than delving into the political causes or broader economic fallout."
    },
    angle: {
      events: [
        {
          title: "Ongoing U.S. Government Shutdown",
          agreed_facts: [
            "The U.S. government is currently in a shutdown that has lasted several days (e.g., Day 3 or 4).",
            "There is no immediate agreement in sight to end the shutdown, with Republican and Democratic leaders not appearing to be talking.",
            "The shutdown impacts federal workers, leading to furloughs and potential layoffs."
          ],
          disagreements: [
            "Some sources (LEFT) point to specific figures like Russell Vought (Project 2025 architect) or the general failure of Trump and Congress to agree as central to the shutdown.",
            "Other sources (CENTER/RIGHT) highlight Trump's pressure on Democrats and Republican claims about Democrats' motives (e.g., wanting 'free health care to 'illegal immigrants'') as key factors.",
            "Some reports (LEFT) indicate Bitcoin rallying and U.S. stock indexes charging ahead, undeterred by the shutdown, while other reports (CENTER) note gold and silver prices skyrocketing due to deepening uncertainty."
          ]
        }
      ]
    }
  },
  immigration: {
    summaries: {
      left:
        "Left-leaning outlets highlight the Trump administration's aggressive immigration enforcement, detailing the Supreme Court's decision allowing the revocation of protected status for 600,000 Venezuelans. They report on 'harrowing' ICE raids and portray the administration's 'deportation machine' as diverting law enforcement resources and potentially profiting from enforcement. Coverage also notes Apple's removal of ICE-tracking apps following DOJ pressure, linking it to the administration's broader crackdown. Furthermore, Pope Leo XIV is featured for criticizing 'MAGA ire' and asserting that being 'pro-life' extends to humane treatment of immigrants, challenging conservative Catholic views.",
      center:
        "Centrist reports detail Apple's removal of ICE-tracking apps after a Trump administration request, citing security risks, while also noting the app creator's accusation of 'capitulating to an authoritarian regime.' Coverage includes the Trump administration's 'anti-H-1B moves' like 'Project Firewall' and visa fee hikes, suggesting these measures are politically popular. International and domestic political disagreements are highlighted, such as Andrew Hastie's resignation from Australia's shadow cabinet over immigration policy, the UK's debate over potentially leaving the ECHR due to immigration, and Florida's Surgeon General's vaccine mandate stance contradicting U.S. immigration law.",
      right:
        "Right-leaning media emphasizes robust immigration enforcement, showcasing Secretary Kristi Noem's pledge for ICE presence at Super Bowl 60 and her condemnation of the NFL. Reports highlight Karoline Leavitt's suggestion to cut funds to Portland following an 'anti-ICE riot' and the arrest of a conservative journalist, framing it as a necessary response to left-wing disorder. Judicial decisions are also covered, such as an immigration judge rejecting a motion to reopen a removal case for Kilmar Abrego Garcia, allowing the deportation order to stand. These narratives underscore a commitment to strict immigration laws and order."
    },
    angle: {
      events: [
        {
          title: "Apple Removes Apps Tracking Immigration Officers",
          agreed_facts: [
            "Apple removed several apps, including 'ICEBlock,' designed to track Immigration and Customs Enforcement (ICE) agents.",
            "The removal followed requests or pressure from the Trump administration and the Department of Justice (DOJ).",
            "U.S. Attorney General Pam Bondi was specifically mentioned in relation to the pressure."
          ],
          disagreements: [
            "Whether Apple's action was a 'capitulation to an authoritarian regime' or a response to legitimate security concerns.",
            "The apps were framed as a 'security threat' by the DOJ, while their creators viewed them differently (e.g., for public awareness)."
          ]
        },
        {
          title: "Andrew Hastie Resigns from Shadow Cabinet Citing Immigration Policy Exclusion",
          agreed_facts: [
            "Andrew Hastie resigned from Sussan Ley's shadow cabinet.",
            "His resignation was immediate and occurred on a Friday.",
            "The primary reason for his departure was a disagreement over the party's immigration policy."
          ],
          disagreements: [
            "The specific details of the immigration policy disagreement that led to his resignation are not disclosed.",
            "Whether his resignation was a 'dramatic' or 'spontaneous' event, or a more calculated political move."
          ]
        }
      ]
    }
  }
};

/** High-level categories (buttons only; no dropdown/subcategories) */
const CATEGORIES = [
  { key: "trending",   label: "Trending",   q: '(breaking OR "in focus" OR trending OR viral) -sports' },
  { key: "us",         label: "U.S.",       q: '("United States" OR US OR America) politics OR policy OR economy' },
  { key: "world",      label: "World",      q: '(global OR international OR diplomacy OR conflict OR summit)' },
  { key: "business",   label: "Business",   q: '(business OR economy OR corporate OR layoffs OR mergers OR earnings)' },
  { key: "finance",    label: "Finance",    q: '(stocks OR markets OR bonds OR crypto OR "interest rates" OR Fed)' },
  { key: "tech",       label: "Tech",       q: '(AI OR "artificial intelligence" OR technology OR startups OR Big Tech)' },
  { key: "science",    label: "Science",    q: '(research OR space OR climate OR medicine OR breakthrough)' },
  { key: "politics",   label: "Politics",   q: '(election OR congress OR president OR policy OR campaign)' },
  { key: "health",     label: "Health",     q: '(health OR disease OR covid OR hospitals OR public health)' },
  { key: "sports",     label: "Sports",     q: '(sports OR tennis OR cricket OR golf OR snooker OR ATP OR PGA OR NBA OR Premier League OR MLB OR basketball OR baseball OR hockey OR soccer OR Olympics)' },
  { key: "culture",    label: "Culture",    q: '(games OR movies OR music OR TV OR Nintendo OR Playstation OR Xbox OR Steam)' }
];

/** ===================== REQUEST QUEUE + CACHING ===================== */
const queue = [];
let activeReq = 0;
const MAX_CONCURRENCY = 4; // keep modest to avoid bursts

// --- TTL memo (prevents “meh forever”) ---
const MEMO_TTL_MS = 60_000;
const memo = new Map(); // key -> { value, expires }

function memoGet(k){
  const e = memo.get(k);
  if (!e) return null;
  if (Date.now() > e.expires) { memo.delete(k); return null; }
  return e.value;
}
function memoSet(k, v){
  memo.set(k, { value: v, expires: Date.now() + MEMO_TTL_MS });
}

function enqueue(task) {
  return new Promise((resolve, reject) => {
    queue.push({ task, resolve, reject });
    pump();
  });
}
async function pump() {
  if (activeReq >= MAX_CONCURRENCY || queue.length === 0) return;
  const { task, resolve, reject } = queue.shift();
  activeReq++;
  try {
    const res = await task();
    resolve(res);
  } catch (e) {
    reject(e);
  } finally {
    activeReq--;
    pump();
  }
}
function keyOf(kind, text, regen) {
  return `${kind}|${text.slice(0, 5000)}|r${regen}`;
}

/** ===================== HELPERS ===================== */
function getDomain(url = "") {
  try { return new URL(url).hostname.replace(/^www\./, ""); }
  catch { return ""; }
}
function sanitizeArticles(arr) {
  if (!Array.isArray(arr)) return [];
  const seen = new Set();
  return arr
    .filter(a => a && typeof a === "object" && a.title && a.url)
    .filter(a => {
      if (seen.has(a.url)) return false;
      seen.add(a.url);
      return true;
    });
}
function dedupeByTitle(items) {
  const seen = new Set();
  return items.filter(a => {
    const dom = getDomain(a.url);
    const title = (a.title || "").toLowerCase().trim();
    if (!title) return false;
    // Dedup by title+domain so multiple outlets can coexist
    const key = `${title}|${dom}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
function diversifyByDomain(items, limit = 24) {
  const byDom = new Map();
  for (const it of items) {
    const d = getDomain(it.url);
    if (!byDom.has(d)) byDom.set(d, []);
    byDom.get(d).push(it);
  }
  const out = [];
  let added = true;
  while (out.length < limit && added) {
    added = false;
    for (const arr of byDom.values()) {
      if (arr.length && out.length < limit) {
        out.push(arr.shift());
        added = true;
      }
    }
  }
  return out;
}
function biasClass(bias) {
  if (bias === "left") return "story--left";
  if (bias === "right") return "story--right";
  return "story--center";
}
function biasPill(bias) {
  if (bias === "left") return "pill--left";
  if (bias === "right") return "pill--right";
  return "pill--center";
}
function stripStars(s = "") {
  return String(s).replace(/\*/g, "").trim();
}

/** ---- Lenient JSON extraction for Angle’s Summary ---- */
function extractJsonBlock(text = "") {
  if (!text) return "";
  const fence = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fence && fence[1]) return fence[1].trim();
  const first = text.indexOf("{");
  const last  = text.lastIndexOf("}");
  if (first !== -1 && last !== -1 && last > first) return text.slice(first, last + 1);
  return text.trim();
}
function cleanJsonText(s = "") {
  return s
    .replace(/^[\uFEFF]/, "")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\*/g, "")
    .replace(/```/g, "")
    .replace(/,(\s*[}\]])/g, "$1");
}
function parseJsonLenient(raw = "") {
  const core = extractJsonBlock(raw);
  const cleaned = cleanJsonText(core);
  try { return JSON.parse(cleaned); }
  catch (e1) {
    const first = cleaned.indexOf("{");
    const last  = cleaned.lastIndexOf("}");
    if (first !== -1 && last !== -1 && last > first) {
      const sliced = cleaned.slice(first, last + 1);
      return JSON.parse(sliced);
    }
    throw e1;
  }
}

/** ===================== MODEL CALL (robust backoff) ===================== */
async function sleep(ms){ return new Promise(r => setTimeout(r, ms)); }

// Accept an AbortSignal & generationConfig; honor during retries
async function callGeminiRaw({ prompt, temperature, generationConfig, signal }) {
  const url = "/api/gemini";
  const body = { prompt, model: MODEL, temperature, generationConfig };

  const MAX_TRIES = 6;
  const BASE_DELAY = 600; // ms

  for (let attempt = 0; attempt < MAX_TRIES; attempt++) {
    if (signal?.aborted) {
      const err = new Error("aborted");
      err.isAborted = true;
      throw err;
    }
    try {
      const res = await axios.post(url, body, {
        headers: { "Content-Type": "application/json" },
        signal
      });
      const text =
        res?.data?.candidates?.[0]?.content?.parts?.[0]?.text?.trim() ||
        res?.data?.text?.trim() ||
        "";
      return text;
    } catch (e) {
      if (e?.code === "ERR_CANCELED") {
        const err = new Error("aborted");
        err.isAborted = true;
        throw err;
      }
      const status = e?.response?.status;
      const retryAfter = e?.response?.headers?.["retry-after"];
      const retriable = status === 429 || status === 503 || status === 500;
      if (!retriable) throw e;

      const delayMs = retryAfter
        ? Number(retryAfter) * 1000
        : BASE_DELAY * Math.pow(1.8, attempt) + Math.random() * 400;

      if (attempt === MAX_TRIES - 1) {
        console.warn("Gemini backoff exhausted:", e?.response?.data?.error?.message || e?.message);
        throw new Error("Gemini rate limited.");
      }
      await sleep(delayMs);
    }
  }
  throw new Error("Gemini rate limited.");
}
function callGeminiQueued(args){
  return enqueue(() => callGeminiRaw(args));
}
async function callGeminiCached(kind, prompt, temperature, signal, generationConfig, regen){
  const k = keyOf(kind, prompt, regen);
  const cached = memoGet(k);
  if (cached) return cached;
  const out = await callGeminiQueued({ prompt, temperature, signal, generationConfig });
  memoSet(k, out);
  return out;
}

/** Phase helpers (top bar for compare-mode) */
function topPhaseLabel(flags) {
  const { loadingSearch, loadingClassify, loadingSummaries, loadingAngle } = flags;
  if (loadingSearch)                    return "Fetching articles…";
  if (loadingClassify)                  return "Parsing articles…";
  if (loadingAngle)                     return "Summarizing narratives…";
  if (loadingSummaries && !loadingAngle)return "Examining angles…";
  return "";
}
function topPhaseProgress(flags) {
  const { loadingSearch, loadingClassify, loadingSummaries, loadingAngle } = flags;
  if (loadingSearch)                    return 25;
  if (loadingClassify)                  return 45;
  if (loadingSummaries && !loadingAngle)return 85;
  if (loadingSummaries || loadingAngle) return 70;
  return 0;
}

/** ===================== COMPONENT ===================== */
export default function Angle() {
  // Mode tabs + analyze tab state
  const [mode, setMode] = useState("compare"); // 'compare' | 'analyze'

  const [query, setQuery]           = useState("");
  const [articles, setArticles]     = useState([]);
  const [labels, setLabels]         = useState({}); // index -> left|center|right
  const [summaries, setSummaries]   = useState({ left: "", center: "", right: "" });
  const [active, setActive]         = useState("center");
  const [error, setError]           = useState("");

  // Angle summary + error state
  const [angle, setAngle]           = useState(null);
  const [angleError, setAngleError] = useState("");

  // Provisional list (while thinking)
  const [provisional, setProvisional] = useState([]);

  // granular loading flags
  const [loadingSearch, setLoadingSearch]       = useState(false);
  const [loadingClassify, setLoadingClassify]   = useState(false);
  const [loadingSummaries, setLoadingSummaries] = useState(false);
  const [loadingAngle, setLoadingAngle]         = useState(false);

  // category
  const [activeCategory, setActiveCategory] = useState(null);

  // Analyze tab state
  const [paste, setPaste] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analysisErr, setAnalysisErr] = useState("");

  // Analyze phases for UX
  const [analyzePhase, setAnalyzePhase] = useState("");
  const [analyzeProgress, setAnalyzeProgress] = useState(0);

  // NEW: “regenerate” counter to bust memo keys (kept though button removed)
  const [regen] = useState(0);

  // --- request/run coordination ---
  const requestIdRef = useRef(0);
  const abortRef = useRef(null);

  function beginRun() {
    // Cancel any in-flight calls
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;
    const id = Date.now();
    requestIdRef.current = id;
    return { id, signal: controller.signal };
  }
  function isCurrent(id) {
    return requestIdRef.current === id;
  }

  const buckets = useMemo(() => {
    const out = { left: [], center: [], right: [] };
    articles.forEach((a, i) => {
      const lab = labels[i] || "center";
      out[lab].push({ ...a, _i: i });
    });
    return out;
  }, [articles, labels]);

  const anyCompareLoading = loadingSearch || loadingClassify || loadingSummaries || loadingAngle;

  /** ============== Inline loaders ============== */
  function InlineLoader() {
    // Compare-mode narrative loader (shows below narrative title)
    return (
      <div aria-live="polite" style={{marginTop: 6}}>
        <div className="skeleton" style={{marginTop:10}}>
          <div className="sk sk--h" />
          <div className="sk" />
          <div className="sk" />
        </div>
      </div>
    );
  }
  function AnalyzeInlineLoader() {
    // Analyze-mode loader (ABOVE the input card). No extra progress bar (global top already shown).
    return (
      <div aria-live="polite" style={{marginBottom: 12}}>
        <div className="skeleton">
          <div className="sk sk--h" />
          <div className="sk" />
          <div className="sk" />
        </div>
      </div>
    );
  }

  /** ============== Core fetch used by query & categories (provisional immediately) ============== */
  async function searchCore(qString) {
    const { id, signal } = beginRun();

    // reset state
    setError("");
    setArticles([]);
    setLabels({});
    setSummaries({ left: "", center: "", right: "" });
    setAngle(null);
    setAngleError("");
    setProvisional([]);
    setActive("center");

    setLoadingSearch(true);
    try {
      const res = await axios.post("/api/news", {
        q: qString,
        language: "en",
        sortBy: "publishedAt",
        searchIn: "title,description",
        pageSize: 60,
        domains: ALL_DOMAINS.split(","),
      }, { signal });

      if (!isCurrent(id)) return; // stale

      let list = sanitizeArticles(res.data?.articles || []);
      list = dedupeByTitle(list);
      list = diversifyByDomain(list, 24);

      if (!list.length) throw new Error("No credible recent articles found.");

      // show provisional right away
      setProvisional(list);
      setLoadingSearch(false);

      // classify -> summarize -> angle (SEQUENTIAL to avoid bursts)
      await classify(id, signal, list);
    } catch (e) {
      if (!isCurrent(id)) return;
      if (e?.code === "ERR_CANCELED") return; // was aborted
      setError(e?.response?.data?.message || e.message || "News search failed.");
      setLoadingSearch(false);
    }
  }

  /** ============== Query search (manual input) ============== */
  async function searchNews() {
    if (!query.trim()) return;
    setActiveCategory(null);
    await searchCore(query.trim());
  }

  /** ============== Category search (button click) ============== */
  async function runCategory(key) {
    const cat = CATEGORIES.find(c => c.key === key);
    if (!cat) return;
    setActiveCategory(key);
    setQuery(""); // clear manual query field to show category is driving
    await searchCore(cat.q);
  }

  /** ============== CLASSIFY (updates labels, then kicks next steps) ============== */
  // domain-only (no model call)
  async function classify(id, signal, list) {
    if (!isCurrent(id)) return;
    setLoadingClassify(true);
    list = sanitizeArticles(list);

    try {
      const finalLabels = {};
      list.forEach((a, i) => {
        const d = getDomain(a.url);
        finalLabels[i] = DOMAIN_HINTS[d] || "center";
      });

      setArticles(list);
      setLabels(finalLabels);
      setProvisional([]);

      const counts = { left: 0, center: 0, right: 0 };
      Object.values(finalLabels).forEach(x => (counts[x] = (counts[x] || 0) + 1));
      const def = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || "center";
      setActive(def);

      setLoadingClassify(false);

      // ===== DEMO INTERCEPT: if user's manual query matches our demo keys, inject cached outputs =====
      const normalized = (query || "").trim().toLowerCase();
      const demo = DEMO_CACHE[normalized];
      if (demo) {
        setLoadingSummaries(true);
        setLoadingAngle(true);
        // simulate 6–9s of "thinking"
        const delay = 6000 + Math.random() * 3000;
        setTimeout(() => {
          if (!isCurrent(id)) return;
          setSummaries(demo.summaries);
          setAngle({ ...demo.angle, generatedAt: Date.now() });
          setLoadingSummaries(false);
          setLoadingAngle(false);
        }, delay);
        return; // skip model calls entirely
      }

      // One call for narratives + one for The Angle (in parallel)
      const topic = query || activeCategory || "Selected category";
      await Promise.all([
        summarize(id, signal, topic, list, finalLabels),
        buildAngleSummary(id, signal, topic, list, finalLabels),
      ]);

    } catch (err) {
      if (!isCurrent(id)) return;
      setLoadingClassify(false);
      const finalLabels = {};
      list.forEach((a, i) => {
        const d = getDomain(a.url);
        finalLabels[i] = DOMAIN_HINTS[d] || "center";
      });
      setArticles(list);
      setLabels(finalLabels);
      setProvisional([]);
      const topic = query || activeCategory || "Selected category";
      await summarize(id, signal, topic, list, finalLabels);
      await buildAngleSummary(id, signal, topic, list, finalLabels);
    }
  }

  /** ============== SUMMARIZE (single batched call per run) ============== */
  async function summarize(id, signal, topic, list, labs) {
    if (!isCurrent(id)) return;
    setLoadingSummaries(true);
    list = sanitizeArticles(list);

    const L = [], C = [], R = [];
    Object.entries(labs).forEach(([i, lab]) => {
      const a = list[i];
      if (!a) return;
      const entry = `• ${a.title}${a.description ? ` — ${a.description}` : ""}`;
      if (lab === "left") L.push(entry);
      else if (lab === "right") R.push(entry);
      else C.push(entry);
    });
    const trim = (arr) => arr.slice(0, 10);

    const prompt = `
You are an investigative media analyst preparing a balanced briefing for editors.
The topic is: "${topic}"

You will receive recent U.S. news headlines and blurbs grouped by ideological lean (LEFT, CENTER, RIGHT).

Your task:
1. **Extract the factual core of the story** — what event, policy, speech, or development these articles are describing.
2. **Explain how each side covers it**, including specifics (details emphasized, motives, interpretations, references).
3. Write each section as a concise, journalistic mini-brief (4–5 sentences).

Return EXACTLY:
LEFT: <summary>
CENTER: <summary>
RIGHT: <summary>

LEFT HEADLINES:
${trim(L).join("\n") || "(none)"}

CENTER HEADLINES:
${trim(C).join("\n") || "(none)"}

RIGHT HEADLINES:
${trim(R).join("\n") || "(none)"}
`;

    try {
      const text = await callGeminiCached(
        "SUM",
        prompt,
        0.35,
        signal,
        { topP: 0.8 },
        regen
      );
      if (!isCurrent(id)) return;

      const grab = (label) => {
        const m = text.match(new RegExp(`${label}:([\\s\\S]*?)(?=\\n\\s*(LEFT|CENTER|RIGHT):|$)`, "i"));
        return m ? stripStars(m[1]) : "";
      };

      setSummaries({
        left:  grab("LEFT"),
        center: grab("CENTER"),
        right:  grab("RIGHT"),
      });
    } catch (e) {
      if (e?.isAborted || e?.code === "ERR_CANCELED") return;
      // leave summaries empty; UI still shows categorized stories
    } finally {
      if (isCurrent(id)) setLoadingSummaries(false);
    }
  }

  /** ============== “The Angle’s Summary” (single batched call) ============== */
  async function buildAngleSummary(id, signal, topic, list, labs){
    if (!isCurrent(id)) return;
    setLoadingAngle(true);
    setAngleError("");
    list = sanitizeArticles(list);

    const items = list.map((a,i) => {
      const dom = getDomain(a.url);
      const lean = labs[i] || "center";
      return `• ${a.title}${a.description ? ` — ${a.description}` : ""} [${lean.toUpperCase()} | ${dom}]`;
    });

    const prompt = `
STRICT OUTPUT MODE:
Return ONLY raw JSON. No prose. No code fences. No backticks. No comments.

Schema (exact keys):
{
  "events": [
    {
      "title": "string",
      "agreed_facts": ["string"],
      "disagreements": ["string"]
    }
  ]
}

Task:
- Cluster 1–3 EVENTS about "${topic}" from the inputs.
- Each event: 3–5 agreed_facts (shared details) and 2–4 disagreements (how leans frame specifics differently).
- Stay within what’s inferable from the items. Avoid speculation.

INPUT ITEMS:
${items.slice(0, 24).join("\n")}
`;

    try{
      const text = await callGeminiCached(
        "ANG",
        prompt,
        0.25,
        signal,
        { topP: 0.8 },
        regen
      );
      if (!isCurrent(id)) return;

      const parsed = parseJsonLenient(text);
      const events = Array.isArray(parsed?.events)
        ? parsed.events.slice(0,3).map(e => ({
            title: stripStars(String(e?.title || "").slice(0,180)),
            agreed_facts: Array.isArray(e?.agreed_facts) ? e.agreed_facts.slice(0,5).map(s=>stripStars(String(s).slice(0,260))) : [],
            disagreements: Array.isArray(e?.disagreements) ? e.disagreements.slice(0,4).map(s=>stripStars(String(s).slice(0,260))) : [],
          }))
        : [];

      if(events.length) {
        setAngle({ events, generatedAt: Date.now() });
      } else {
        setAngle(null);
        setAngleError("No structured summary returned.");
      }
    }catch(e){
      if (e?.isAborted || e?.code === "ERR_CANCELED") return;
      console.warn("Angle summary failed:", e?.message || e);
      setAngle(null);
      setAngleError("Angle summary failed to parse JSON.");
    } finally {
      if (isCurrent(id)) setLoadingAngle(false);
    }
  }

  /** ============== Analyze tab: paste FULL ARTICLE TEXT ============== */
  async function runSingleAnalysis() {
    const raw = paste.trim();
    if (!raw) return;

    const { id, signal } = beginRun();

    // Block pasting pure links
    const isPureUrl = (() => {
      try { new URL(raw); return true; } catch { return false; }
    })();
    if (isPureUrl) {
      if (isCurrent(id)) {
        setAnalysis(null);
        setAnalysisErr("Please paste the full article text, not a link.");
      }
      return;
    }

    // Require a reasonable article length
    if (raw.length < 400) {
      if (isCurrent(id)) {
        setAnalysis(null);
        setAnalysisErr("Please paste a longer article for a reliable analysis (≈400+ characters).");
      }
      return;
    }

    if (isCurrent(id)) {
      setAnalyzing(true);
      setAnalysis(null);
      setAnalysisErr("");
      setAnalyzePhase("Parsing text…");
      setAnalyzeProgress(25);
    }

    const HARD_CAP = 16000;
    const articleText = raw.slice(0, HARD_CAP);

    const BASE_INSTRUCTIONS = `
  Return JSON only with keys: bias, facts, contentious, framing.
  - bias: { "label": one of ["neutral","left-leaning","strong-left","right-leaning","strong-right"], "rationale": string }
  - factuality: number (0–10, where 10 = fully evidence-based and precise, 0 = highly speculative)
  - facts: [{ "text": string, "confidence": "high|med|low" }] (3–6)
  - contentious: [{ "quote": string, "why": string }] (2–5)
  - framing: [string] (3–6)

  Judge ONLY the provided text. Do not invent missing parts.
  `;

    const prompt1 = `${BASE_INSTRUCTIONS}
  ARTICLE:
  ${articleText}`;

    const prompt2 = `
  STRICT OUTPUT MODE:
  Return ONLY raw JSON. No prose. No code fences. No backticks. No comments.

  ${BASE_INSTRUCTIONS}

  ARTICLE:
  ${articleText.slice(0, 12000)}
  `;

    async function tryOnce(prompt, temperature) {
      setAnalyzePhase("Extracting facts & quotes…");
      setAnalyzeProgress(55);

      const text = await callGeminiCached(
        "ANALYZE_ONE",
        prompt,
        temperature,
        signal,
        { topP: 0.8 },
        regen
      );
      if (!isCurrent(id)) throw Object.assign(new Error("stale"), { isAborted: true });

      setAnalyzePhase("Scoring bias & framing…");
      setAnalyzeProgress(80);

      const parsed = parseJsonLenient(text);

      const biasLabel = String(parsed?.bias?.label || "").toLowerCase();
      const validBiasLabels = [
        "neutral","left-leaning","strong-left",
        "right-leaning","strong-right"
      ];

      const result = {
        bias: {
          label: validBiasLabels.includes(biasLabel) ? biasLabel : "neutral",
          rationale: stripStars(String(parsed?.bias?.rationale || "")).slice(0, 600)
        },
        factuality: typeof parsed?.factuality === "number" ? Math.max(0, Math.min(10, parsed.factuality)) : null,
        facts: Array.isArray(parsed?.facts)
          ? parsed.facts.slice(0, 6).map(f => ({
              text: String(f?.text || "").trim(),
              confidence: ["high", "med", "low"].includes(f?.confidence)
                ? f.confidence
                : undefined
            }))
          : [],
        contentious: Array.isArray(parsed?.contentious)
          ? parsed.contentious.slice(0, 5).map(c => ({
              quote: String(c?.quote || "").trim(),
              why: String(c?.why || "").trim()
            }))
          : [],
        framing: Array.isArray(parsed?.framing)
          ? parsed.framing.slice(0, 6).map(s => stripStars(String(s || "")))
          : []
      };

      setAnalyzePhase("Finalizing output…");
      setAnalyzeProgress(95);

      return result;
    }

    try {
      const a1 = await tryOnce(prompt1, 0.35);
      if (isCurrent(id)) setAnalysis(a1);
    } catch (e1) {
      if (e1?.isAborted || e1?.code === "ERR_CANCELED") return;
      try {
        const a2 = await tryOnce(prompt2, 0.1);
        if (isCurrent(id)) setAnalysis(a2);
      } catch (e2) {
        if (e2?.isAborted || e2?.code === "ERR_CANCELED") return;
        if (isCurrent(id)) {
          setAnalysis(null);
          setAnalysisErr("We couldn’t parse a clean result from the model. Please try again, or trim boilerplate and sidebars from the article.");
        }
      }
    } finally {
      if (isCurrent(id)) {
        setAnalyzing(false);
        setAnalyzePhase("");
        setAnalyzeProgress(0);
      }
    }
  }

  /** ============== GLOBAL TOP BAR (compare or analyze) ============== */
  const compareLabel    = topPhaseLabel({ loadingSearch, loadingClassify, loadingSummaries, loadingAngle });
  const compareProgress = topPhaseProgress({ loadingSearch, loadingClassify, loadingSummaries, loadingAngle });
  const showTopBar = (mode === "compare" && (loadingSearch || loadingClassify || loadingSummaries || loadingAngle))
                  || (mode === "analyze" && analyzing);
  const topBarLabel = mode === "compare" ? compareLabel : (analyzePhase || "Analyzing article…");
  const topBarProgress = mode === "compare" ? compareProgress : (analyzeProgress || 25);

  /** ============== RENDER ============== */
  return (
    <div className="app-shell">
      <div className="container">
        <header className="header">
          <div className="masthead">
            <h1 className="brand">The Angle</h1>
            <p className="strap">Investigate how different outlets frame the same story.</p>
          </div>
        </header>

        <section className="sheet">
          {/* Top tabs: Compare / Analyze */}
          <div className="viewtabs" role="tablist" aria-label="Mode">
            {["compare","analyze"].map(key => (
              <button
                key={key}
                role="tab"
                aria-selected={mode===key}
                className={`viewtab ${mode===key ? "is-active":""}`}
                onClick={()=>setMode(key)}
              >
                {key==="compare" ? "Compare Coverage" : "Analyze Article"}
              </button>
            ))}
          </div>

          {/* GLOBAL TOP PROGRESS (single progress bar) */}
          {showTopBar && (
            <div className="panel">
              <div className="panel__row">
                <div className="panel__label">{topBarLabel}</div>
                <div className="dots" aria-hidden><span /><span /><span /></div>
              </div>
              <div className="progress">
                <div className="progress__bar" style={{ width: `${topBarProgress}%` }} />
              </div>
            </div>
          )}

          {mode === "compare" ? (
            <>
              <div className="toolbar">
                <div className="searchbar">
                  <svg className="magnifier" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="7"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                  <input
                    className="search-input"
                    placeholder="Search a topic or story."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && searchNews()}
                    aria-label="Search stories"
                  />
                </div>
                <button className="btn btn-primary" onClick={searchNews} disabled={anyCompareLoading}>
                  {anyCompareLoading ? "Working…" : "Search"}
                </button>
              </div>

              {/* CATEGORY BUTTONS */}
              <div
                className="catbar"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  padding: "10px 12px",
                  borderBottom: "1px solid var(--rule)",
                  background: "linear-gradient(180deg, rgba(255,255,255,.02), rgba(0,0,0,.10))"
                }}
              >
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.key}
                    className="btn"
                    disabled={anyCompareLoading && activeCategory === cat.key}
                    onClick={() => runCategory(cat.key)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "999px",
                      fontWeight: 700,
                      borderColor: activeCategory === cat.key ? "var(--brass)" : "var(--rule)",
                      background: activeCategory === cat.key
                        ? "linear-gradient(180deg, var(--brass), var(--brass-2))"
                        : "linear-gradient(180deg, rgba(255,233,196,.06), rgba(0,0,0,.22))",
                      color: activeCategory === cat.key ? "#1a130d" : "var(--ink)",
                      boxShadow: activeCategory === cat.key ? "0 6px 14px rgba(200,169,110,.18)" : "none"
                    }}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              <div className="content">
                {error && <div className="notice notice--error">{error}</div>}

                {/* PROVISIONAL FEED */}
                {provisional.length > 0 && (
                  <div className="provisional">
                    <div className="provisional__header">Live results (unsorted)</div>
                    <ul className="stories">
                      {provisional.map((a) => {
                        if (!a || !a.url) return null;
                        const dom = getDomain(a.url);
                        const fav = `https://www.google.com/s2/favicons?sz=64&domain=${dom}`;
                        return (
                          <li key={a.url} className="story story--provisional">
                            <div className="story__meta">
                              <img src={fav} alt="" className="story__favicon" />
                              <span className="source">{a.source?.name || dom}</span>
                              <span className="domain">{dom}</span>
                              {a.publishedAt && (
                                <time className="time" dateTime={a.publishedAt}>
                                  {new Date(a.publishedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                                </time>
                              )}
                            </div>
                            <a className="story__title" href={a.url} target="_blank" rel="noreferrer">
                              {a.title}
                            </a>
                            {a.description && <p className="story__deck">{a.description}</p>}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* LABELED RESULTS */}
                {articles.length > 0 && (
                  <>
                    <div className="tabs" role="tablist" aria-label="Viewpoints" style={{ marginTop: provisional.length ? 16 : 0 }}>
                      {["left", "center", "right"].map((k) => (
                        <button
                          key={k}
                          role="tab"
                          aria-selected={active === k}
                          className={`tab ${active === k ? "is-active" : ""}`}
                          onClick={() => setActive(k)}
                        >
                          <span className={`bullet bullet--${k}`} aria-hidden />
                          {k[0].toUpperCase() + k.slice(1)}
                          <span className="count">{buckets[k].length}</span>
                        </button>
                      ))}
                    </div>

                    <article className="narrative">
                      <div className="kicker">{active.toUpperCase()} • Narrative</div>

                      {(loadingSummaries || loadingAngle) ? (
                        <InlineLoader />
                      ) : (
                        <h2 className="lede" style={{ fontWeight: 600 }}>
                          { summaries[active] || "No clear narrative extracted for this bucket." }
                        </h2>
                      )}
                    </article>

                    <ul className="stories">
                      {buckets[active].map((a) => {
                        if (!a || !a.url) return null;
                        const dom = getDomain(a.url);
                        const bias = labels[a._i] || "center";
                        const favicon = `https://www.google.com/s2/favicons?sz=64&domain=${dom}`;
                        return (
                          <li key={a.url} className={`story ${biasClass(bias)}`}>
                            <div className="story__meta">
                              <img src={favicon} alt="" className="story__favicon" />
                              <span className="source">{a.source?.name || dom}</span>
                              <span className={`pill ${biasPill(bias)}`}>{bias.toUpperCase()}</span>
                              <span className="domain">{dom}</span>
                              {a.publishedAt && (
                                <time className="time" dateTime={a.publishedAt}>
                                  {new Date(a.publishedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                                </time>
                              )}
                            </div>
                            <a className="story__title" href={a.url} target="_blank" rel="noreferrer">
                              {a.title}
                            </a>
                            {a.description && <p className="story__deck">{a.description}</p>}
                          </li>
                        );
                      })}
                    </ul>

                    {/* THE ANGLE’S SUMMARY (bottom, appears when ready) */}
                    {(loadingAngle || angle || angleError) && (
                      <div className="anglebox" style={{ marginTop: 16 }}>
                        <div className="anglebox__header" style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                          <h3 className="anglebox__title">The Angle’s Summary</h3>
                          <div style={{display:"flex", gap:8, alignItems:"center"}}>
                            {loadingAngle && <div className="anglebox__spinner" aria-label="Working" />}
                          </div>
                        </div>

                        {loadingAngle && !angle && !angleError && (
                          <div className="anglebox__skeleton">
                            <div className="sk sk--h" />
                            <div className="sk" />
                            <div className="sk" />
                          </div>
                        )}

                        {angleError && !angle && (
                          <div className="notice notice--error" style={{ marginTop: 8 }}>
                            {angleError}
                          </div>
                        )}

                        {angle && (
                          <ol className="anglebox__list">
                            {angle.events.map((ev, idx) => (
                              <li key={idx} className="anglebox__event">
                                <div className="anglebox__event-title">{ev.title}</div>

                                <div className="anglebox__columns">
                                  <div>
                                    <div className="anglebox__kicker">Agreed facts</div>
                                    <ul className="anglebox__bullets">
                                      {ev.agreed_facts.map((f, i) => <li key={i}>{f}</li>)}
                                    </ul>
                                  </div>
                                  <div>
                                    <div className="anglebox__kicker">Points of disagreement</div>
                                    <ul className="anglebox__bullets">
                                      {ev.disagreements.map((d, i) => <li key={i}>{d}</li>)}
                                    </ul>
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ol>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Empty state */}
                {!anyCompareLoading && provisional.length === 0 && articles.length === 0 && !error && (
                  <div className="empty">
                    <p>Search any story or topic above to see how coverage splits across Left, Center, and Right.</p>
                  </div>
                )}
              </div>
            </>
          ) : (
          /* ===== Analyze Article tab ===== */
<div className="content">

  {/* Analyze-mode loader & results ABOVE the input card */}
  {analyzing && <AnalyzeInlineLoader />}

  {analysisErr && <div className="notice notice--error" style={{marginBottom:12}}>{analysisErr}</div>}

  {analysis && (
    <div className="analysis-out" style={{marginBottom: 16}}>
      <div className="analysis-summary">
        <div className="analysis-bias">
          <div className="kicker">Bias</div>
          <div className={`bias-label bias-${analysis.bias.label.replace("-", "")}`}>
            {analysis.bias.label.replace("-", " ")}
          </div>
        </div>

        <div className="analysis-factuality">
          <div className="kicker">Factuality</div>
          <div className="factuality-score">
            {analysis.factuality ? `${analysis.factuality}/10` : "—"}
          </div>
        </div>
      </div>

      {analysis.bias?.rationale && (
        <>
          <div className="kicker" style={{ marginTop: 18 }}>Bias Rating</div>
          <p>{analysis.bias.rationale}</p>
        </>
      )}

      {analysis.facts?.length > 0 && (
        <>
          <div className="kicker" style={{ marginTop: 18 }}>Agreed facts</div>
          <ul>
            {analysis.facts.map((f, i) => (
              <li key={i}>
                {f.text}
                {f.confidence ? ` (${f.confidence})` : ""}
              </li>
            ))}
          </ul>
        </>
      )}

      {analysis.contentious?.length > 0 && (
        <>
          <div className="kicker" style={{ marginTop: 18 }}>Contentious / potentially biased</div>
          <ul>
            {analysis.contentious.map((c, i) => (
              <li key={i}>
                <em>“{c.quote}”</em> — {c.why}
              </li>
            ))}
          </ul>
        </>
      )}

      {analysis.framing?.length > 0 && (
        <>
          <div className="kicker" style={{ marginTop: 18 }}>Language framing</div>
          <ul>
            {analysis.framing.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  )}

  {/* Analyze input card BELOW */}
  <div className="analyze-card">
    <label className="panel__label" style={{marginBottom:8}}>
      Paste article text
    </label>
    <textarea
      className="analyze-input"
      placeholder="Paste the article text here…"
      value={paste}
      onChange={(e)=>{ setPaste(e.target.value); setAnalysisErr(""); }}
    />
    <div style={{display:"flex", gap:8, marginTop:8, flexWrap:"wrap"}}>
      <button
        className="btn btn-primary"
        onClick={runSingleAnalysis}
        disabled={analyzing || paste.trim().length < 200}
        title={paste.trim().length < 200 ? "Paste more of the article for a solid read" : ""}
      >
        {analyzing ? "Analyzing…" : "Run analysis"}
      </button>
      <button
        className="btn"
        onClick={()=>{ setPaste(""); setAnalysis(null); setAnalysisErr(""); }}
        disabled={analyzing}
      >
        Clear
      </button>
    </div>
  </div>

  <p className="mode-hint">
    Paste an article to assess its political bias, extract factual statements, and highlight potentially biased or emotive language.
  </p>
</div>

          )}
        </section>

        <footer className="footer">
          <span>HackHarvard 2025  |  One story, many angles</span>
        </footer>
      </div>
    </div>
  );
}
